/* eslint-disable no-undef */
import template from "./modal.html";
import {
	find,
	compact,
	debounce
} from "lodash";
import polygonCenter from "geojson-polygon-center";
import {
	FIELD_KEYS,
	EVENT_LOGOUT
} from "constants.js";
import { oxford } from "humanize-plus";
import { UNITS } from "../../../constants";
import { idsMatch } from "../../../utils/valueUtilities";
import reefIcon from "../../../images/reef.svg";
import { toFeatures } from "../../../utils/geometries-to-geojson.js";

const settings = {
	pixelOffset: {
		width: 0,
		height: -45
	}
};

export class Controller {
	lookup = null;
	mapInstance = null;
	currentWindowPosition = null;
	infoWindow = new window.google.maps.InfoWindow(settings);
	marker = new google.maps.Marker();
	selectedFeatures = {};
	selectedValueByFieldId = {};

	selected = {
		infoWindow: new window.google.maps.InfoWindow(settings),
		feature: null,
		marker: new google.maps.Marker()
	};

	constructor($scope, mapHelpers, $base64, toastr, eventBus, $logger) {
		this._scope = $scope;
		this._mapHelpers = mapHelpers;
		this._base64 = $base64;
		this._toastr = toastr;
		this.eventBus = eventBus;
		this.$logger = $logger;
	}

	$onInit() {
		this.lastEnteredArea = null;
		this.debouncedLoadElevation = debounce(this.loadElevation, 60);

		this.lookup = this.resolve.lookup;
		this.selectedValueByFieldId = (this.resolve.selectedValues ?? []).reduce((valuesByField, value) => {
			valuesByField[value.fieldId] = value;
			return valuesByField;
		}, {});
		this.title = oxford(this.lookup.data.map(d => d.Name));
		this.initializeMap();
		this.setMapLayers();

		this.eventBus.register(EVENT_LOGOUT, () => { this.handleSelection(); }, this._scope);
	}

	getSelectionPrompt() {
		const names = (this.lookup.data ?? []).reduce((selectionNames, field) => {
			if(field.Name) {
				selectionNames.push(field.Name);
			}

			return selectionNames;
		}, []);

		if(names.length === 1) {
			return `After selecting a ${names[0]}, please click Done.`;
		}

		if(names.length > 1) {
			return `After selecting a ${names.slice(0, -1).join(", ")} and/or ${names[names.length - 1]}, please click Done.`;
		}

		return "After selecting the relevant values, please click Done.";
	}

	handleSelection() {
		const location = find(this.lookup.data, f => f.Key.Code === FIELD_KEYS.location && !f.value);

		if(location) {
			this._toastr.error(`Please select a ${location.Name} before hitting Done.`);
			return;
		}

		const values = compact(this.lookup.data.map(f => f.value));

		this.modalInstance.close(values);
	}

	setHoverStyle(event) {
		this.revertStyle();
		this.layer.overrideStyle(event.feature, {
			fillOpacity: 0.8
		});
	}

	revertStyle() {
		this.layer.revertStyle();

		Object.keys(this.selectedFeatures).forEach((fieldId) => {
			const selectedFeature = this.selectedFeatures[fieldId];

			selectedFeature && this.layer.overrideStyle(selectedFeature, {
				fillOpacity: 0.8
			});
		});
	}

	loadElevation(point, name) {
		return this._mapHelpers.elevationLoader.load(point).then(result => {
			const elevation = result;
			const content = `
              <h4>${name}</h4>
			 	 <small>
					${elevation?.isAboveSeaLevel ? "Elevation": "Depth"}: 
					${Math.round(elevation.meters)} ${UNITS.meter.abbreviation}
					(${Math.round(elevation.feet)} ${UNITS.feet.abbreviation}.)
				</small>
			`;

			if(this.currentWindowPosition == JSON.stringify(point)) {
				this.showInfoWindow(content, point);
			}
			return result;
		});
	}

	getFeaturePoint(feature) {
		const geometry = feature?.geometry;

		if(!geometry) {
			return null;
		}

		if(geometry.type === "Point") {
			return {
				lng: geometry.coordinates[0],
				lat: geometry.coordinates[1]
			};
		}

		if(geometry.type === "Polygon") {
			const center = polygonCenter(geometry);

			return {
				lng: center.coordinates[0],
				lat: center.coordinates[1]
			};
		}

		if(geometry.type === "MultiPolygon") {
			return this.getCoordinatesCenter(geometry.coordinates);
		}

		return null;
	}

	getCoordinatesCenter(coordinates) {
		const bounds = {
			minLng: Infinity,
			maxLng: -Infinity,
			minLat: Infinity,
			maxLat: -Infinity
		};

		const updateBounds = (values) => {
			if(!Array.isArray(values)) {
				return;
			}

			if(values.length === 2 && values.every(value => typeof value === "number")) {
				const [lng, lat] = values;

				bounds.minLng = Math.min(bounds.minLng, lng);
				bounds.maxLng = Math.max(bounds.maxLng, lng);
				bounds.minLat = Math.min(bounds.minLat, lat);
				bounds.maxLat = Math.max(bounds.maxLat, lat);
				return;
			}

			values.forEach(updateBounds);
		};

		updateBounds(coordinates);

		if(!isFinite(bounds.minLng) || !isFinite(bounds.minLat)) {
			return null;
		}

		return {
			lng: (bounds.minLng + bounds.maxLng) / 2,
			lat: (bounds.minLat + bounds.maxLat) / 2
		};
	}

	getEvents() {
		const ctrl = this;
		const isAreaFeature = (feature) => feature?.geometry?.type === "Polygon" || feature?.geometry?.type === "MultiPolygon";

		return {
			mouseover(event) {
				const name = event.feature.getProperty("name");
				const feature = event.feature.getProperty("feature");
				const point = ctrl.getFeaturePoint(feature);

				if(!point) {
					return;
				}

				if(isAreaFeature(feature)) {
					this.lastEnteredArea = event.feature;
				}

				const content = `
					<h4>${name}</h4>
					<small>Depth: Loading...</small>
				`;
				ctrl.showInfoWindow(content, point);

				ctrl.setHoverStyle(event);
				ctrl.debouncedLoadElevation(point, name);
			},
			mouseout() {
				ctrl.revertStyle();
				ctrl.currentWindowPosition = null;
				ctrl.infoWindow.close();
			},
			async click(event) {
				const name = event.feature.getProperty("name");
				const feature = event.feature.getProperty("feature");
				const point = ctrl.getFeaturePoint(feature);

				if(!point) {
					return;
				}

				if(isAreaFeature(feature)) {
					this.lastEnteredArea = event.feature;
				}
				
				// this purposefully does not use the debounced version, to avoid skipping the call
				const elevation = await ctrl.loadElevation(point, name);

				const content = `<h4>${name}</h4>
			 	 <small>
					${elevation.isAboveSeaLevel > 0? "Elevation": "Depth"}: 
					${Math.round(elevation.meters)} ${UNITS.meter.abbreviation}
					(${Math.round(elevation.feet)} ${UNITS.feet.abbreviation}.)
				</small>`;

				ctrl.selected.feature = event.feature;

				ctrl.selected.infoWindow.setContent(content);
				ctrl.selected.infoWindow.setPosition(point);
				ctrl.selected.infoWindow.open(ctrl.mapInstance);

				ctrl.marker.setPosition(point);
				ctrl.marker.setMap(ctrl.mapInstance);

				setFieldValueForFeature(event.feature);

				if(event.feature.getProperty("feature").geometry.type === "Point" && !!this.lastEnteredArea) {
					setFieldValueForFeature(this.lastEnteredArea);
				}


				ctrl._scope.$evalAsync();
			}
		};

		function setFieldValueForFeature(feature) {
			const fieldId = feature.getProperty("fieldId");
			const id = feature.getProperty("id");
			const name = feature.getProperty("name");

			const field = find(ctrl.lookup.data, (f) => f.Id === fieldId);

			field.value = {
				Id: id,
				Name: name,
				fieldId: fieldId
			};

			ctrl.selectedValueByFieldId[fieldId] = field.value;
			ctrl.selectedFeatures[fieldId] = feature;

			if(field.Key?.Code === FIELD_KEYS.location) {
				ctrl.selected.feature = feature;
			}
		}
	}

	showInfoWindow(content, coordinates) {
		this.currentWindowPosition = JSON.stringify(coordinates);
		this.infoWindow.setContent(content);

		this.infoWindow.setPosition(new window.google.maps.LatLng(coordinates));

		this.infoWindow.open(this.mapInstance);
	}

	initializeMap() {
		const ctrl = this;
		const map = new google.maps.Map(document.getElementById("map"), {
			gestureHandling: "greedy",
			streetViewControl: false
		});

		map.setMapTypeId(window.google.maps.MapTypeId.HYBRID);
		window.google.maps.event.trigger(map, "resize");

		setTimeout(function() {
			google.maps.event.trigger(map, "resize");

			const bounds = new google.maps.LatLngBounds();
			ctrl.layer.forEach(function(feature) {
				feature.getGeometry().forEachLatLng(function(latlng) {
					bounds.extend(latlng);
				});
			});

			ctrl.mapInstance.fitBounds(bounds);

			const zoom = ctrl.mapInstance.getZoom();
			ctrl.mapInstance.setZoom(zoom + 1);

		}, 600);

		this.mapInstance = map;
	}

	clearSelected(fieldId) {
		const f = find(this.lookup.data, d => d.Id === fieldId);
		delete this.selectedFeatures[fieldId];
		delete this.selectedValueByFieldId[fieldId];

		if(f.Key.Code === FIELD_KEYS.location) {
			this.selected.infoWindow.close();
			this.selected.feature = null;
		}

		f.value = null;
		this.revertStyle();
		this._scope.$evalAsync();
	}

	hydrateSelectedFeatures() {
		if(!this.layer || typeof this.layer.forEach !== "function") {
			return;
		}

		this.selectedFeatures = {};

		this.layer.forEach((feature) => {
			const fieldId = feature.getProperty("fieldId");
			const id = feature.getProperty("id");
			const savedValue = this.selectedValueByFieldId[fieldId];

			if(savedValue && idsMatch(savedValue.Id, id)) {
				this.selectedFeatures[fieldId] = feature;
			}
		});

		const locationField = find(this.lookup.data, field => field.Key?.Code === FIELD_KEYS.location);

		if(locationField && this.selectedFeatures[locationField.Id]) {
			this.selected.feature = this.selectedFeatures[locationField.Id];
		}

		this.revertStyle();
	}

	setMapLayers() {
		this.layer = new google.maps.Data();

		this.lookup.data.forEach((f) => {
			f.value = this.selectedValueByFieldId[f.Id] ?? null;
			this.addToLayer(f);
		});

		this.layer.setStyle((param) => {
			const feature = param.getProperty("feature");

			return {
				strokeColor: "#9cbcd8",
				strokeOpacity: 0.8,
				strokeWeight: 1,
				fillColor: "#9cbcd8",
				fillOpacity: 0.0,
				optimized: false,
				icon: feature.geometry.type == "Point"
					? {
						url: reefIcon,
						scaledSize: new google.maps.Size(16, 16)
					}
					: ""
			};
		});

		this.layer.setMap(this.mapInstance);

		const events = this.getEvents();

		events.mouseover && this.layer.addListener("mouseover", events.mouseover);
		events.mouseout && this.layer.addListener("mouseout", events.mouseout);
		events.click && this.layer.addListener("click", events.click);

		this.hydrateSelectedFeatures();
	}

	extractGeoJson(item, fieldId) {
		const geoJson = JSON.parse(this._base64.decode(item.Shape.GeoJson));

		geoJson.features.map(f => {
			return f.properties = {
				...f.properties,
				id: item.Id,
				fieldId: fieldId,
				name: item.Name ?? item.Code,
				feature: f
			};
		});

		return geoJson;
	}

	addToLayer(field) {
		const fieldDataGeoJson = (field.Data ?? []).reduce((collection, d) => {
			let geoJson = null;

			if(d.Shape?.GeoJson) {
				geoJson = this.extractGeoJson(d, field.Id);
			} 
			else if(d.Geometries && d.Geometries.length > 0) {
				geoJson = this.extractGeometries(d);
			}

			if(geoJson) {
				geoJson.features
					.filter(f => f.type === "Feature")
					.forEach(f => collection.singleFeatureCollection.features.push(toFeatures(f, {
						id: d.Id,
						fieldId: field.Id,
						name: d.Name ?? d.Code
					}, this.$logger)[0]));

				let featureCollections = geoJson.features.filter(f => f.type === "FeatureCollection");
				featureCollections = featureCollections.map(fc => toFeatures(fc, {
					id: d.Id,
					fieldId: field.Id,
					name: d.Name ?? d.Code
				}, this.$logger));

				collection.featureCollections.push(...featureCollections);
			}

			return collection;
		}, { singleFeatureCollection: { type: "FeatureCollection", features: [] }, featureCollections: [] });

		if(fieldDataGeoJson.singleFeatureCollection.features.length) {
			this.addGeoJsonToLayer({ geoJson: fieldDataGeoJson.singleFeatureCollection });
		}

		if(fieldDataGeoJson.featureCollections.length) {
			fieldDataGeoJson.featureCollections.forEach(fc => {
				this.addGeoJsonToLayer(fc);
			});
		}
	}

	addGeoJsonToLayer(geoJsonWrapper) {
		// google maps api only accepts features or feature collections, so we need to wrap the geometry in a feature if it's not already one
		if(geoJsonWrapper.geoJson.type !== "Feature" && geoJsonWrapper.geoJson.type !== "FeatureCollection") {
			const ex = new Error("GeoJSON value must be a Feature or FeatureCollection");
			this.$logger.exception(ex, { geoJson: geoJsonWrapper });
		}

		this.layer.addGeoJson(geoJsonWrapper.geoJson);
	}

	extractGeometries(dataItem) {
		const features = (dataItem.Geometries ?? [])
			.filter((geom) => geom?.Type?.Code === "GEOJSON")
			.map((geom) => {
				return geom.Definition;
			});

		return { features };
	}
}

export default angular
	.module("vesl.components.field-helpers.map-modal", [])
	.component("mapModal", {
		controller: Controller,
		template,
		bindings: {
			close: "&",
			dismiss: "&",
			modalInstance: "<",
			resolve: "<"
		}
	}).name;
